import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { api } from '@/api';

// Logo URL from public assets
const LOGO_URL = '/gantt-logo-blue.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.auth.forgotPassword(email);
      setEmailSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      toast.error(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#f4f7fa' }}>
        <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-sm border border-slate-100">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-barlow font-bold uppercase mb-2">Check Your Email</h2>
            <p className="text-slate-600 mb-6">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              The link will expire in 24 hours. If you don't see the email, check your spam folder.
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: '#f4f7fa' }}>
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow-sm border border-slate-100">
        <div>
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} alt="Gantt" className="h-28 w-auto" />
          </div>
          <h2 className="text-2xl font-barlow font-bold uppercase text-center mb-2">Forgot Password</h2>
          <p className="text-slate-600 text-center">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="email"
                data-testid="forgot-password-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 rounded-sm border-slate-200"
                placeholder="Enter your email"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid="forgot-password-submit-button"
            className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>

          <div className="text-center">
            <Link 
              to="/login" 
              className="text-sm text-slate-600 hover:text-slate-800 hover:underline inline-flex items-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
